import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import SideNav from '../../Components/SideNav';
import Loader from '../../Components/Loader';
import BarraSeccion from '../../Components/BarraSeccion';
import ListaAdmin from '../../Components/ListaAdmin';
import FilaAdmin, { Insignia } from '../../Components/FilaAdmin';
import Paginacion from '../../Components/Paginacion';

import useListaPaginada from '../../Hooks/useListaPaginada';

import { CUATRIMESTRES, etiquetaLicenciatura, formatearFecha } from '../../Helpers/solicitudes';

const CLAVES = ['licenciatura', 'anio', 'cuatrimestre', 'estado'];
const FIJOS = { archivado: 'true' };

const ESTADOS = [
    { valor: undefined, etiqueta: 'Todas' },
    { valor: 'pendiente', etiqueta: 'Pendientes' },
    { valor: 'atendido', etiqueta: 'Atendidas' }
];

const cuantas = n => n.toLocaleString('es-MX') + ' ' + (n === 1 ? 'solicitud' : 'solicitudes');

// Solicitudes archivadas. Dos modos (?vista=carpetas): lista con filtros (por defecto) o carpetas
// licenciatura → año y cuatrimestre → lista. Los dos usan los mismos parámetros de la URL
const VistaArchivo = ({ helper, encabezado, licenciaturas, rutaDetalle, extraFila }) => {
    const [params, setParams] = useSearchParams();
    const [carpetas, setCarpetas] = useState(null);

    const enCarpetas = params.get('vista') === 'carpetas';
    const conLicenciatura = params.has('licenciatura');
    const conCuatrimestre = params.has('anio') && params.has('cuatrimestre');
    // En carpetas, la lista solo se pide al abrir una carpeta de cuatrimestre
    const enLista = !enCarpetas || (conLicenciatura && conCuatrimestre);

    const lista = useListaPaginada({ obtener: helper.listar, claves: CLAVES, fijos: FIJOS, activo: enLista });

    useEffect(() => {
        helper.carpetas(true).then(res => {
            if (res.success) setCarpetas(res.data);
            else Swal.fire('Error!', 'No se pudieron obtener las carpetas, codigo: ' + res.code, 'error');
        });
    }, []);

    const { licenciatura, anio, cuatrimestre, estado } = lista.filtros;

    const anios = useMemo(() => [...new Set((carpetas ?? []).map(c => c.anio))].sort((a, b) => b - a), [carpetas]);

    const porLicenciatura = useMemo(() => {
        const totales = new Map();
        (carpetas ?? []).forEach(c => totales.set(c.licenciatura, (totales.get(c.licenciatura) ?? 0) + c.total));
        // Primero las licenciaturas conocidas en su orden; luego cualquier otra que aparezca en los datos
        const conocidas = licenciaturas.map(l => l.valor).filter(valor => totales.has(valor));
        const otras = [...totales.keys()].filter(valor => !licenciaturas.some(l => l.valor === valor));
        return [...conocidas, ...otras].map(valor => ({ valor, total: totales.get(valor) }));
    }, [carpetas, licenciaturas]);

    const porAnio = useMemo(() => {
        if (!conLicenciatura) return [];
        const grupos = new Map();
        (carpetas ?? []).filter(c => c.licenciatura === licenciatura).forEach(c => {
            if (!grupos.has(c.anio)) grupos.set(c.anio, []);
            grupos.get(c.anio).push(c);
        });
        return [...grupos.entries()].sort((a, b) => b[0] - a[0]).map(([anioGrupo, delAnio]) => ({ anio: anioGrupo, carpetas: delAnio.sort((a, b) => a.cuatrimestre - b.cuatrimestre) }));
    }, [carpetas, conLicenciatura, licenciatura]);

    // Cambiar de modo o de carpeta reemplaza todos los parámetros (filtros, búsqueda y página)
    const ir = nuevos => {
        lista.setBusqueda('');
        setParams(new URLSearchParams(Object.entries(nuevos).filter(([, valor]) => valor !== undefined)));
    };

    const nombreLicenciatura = valor => etiquetaLicenciatura(licenciaturas, valor);

    const accionesModo = (
        <div className='segmentado_admin' role='group' aria-label='Modo de vista'>
            <button type='button' aria-pressed={!enCarpetas} onClick={() => enCarpetas && ir({})}>Lista</button>
            <button type='button' aria-pressed={enCarpetas} onClick={() => !enCarpetas && ir({ vista: 'carpetas' })}>Carpetas</button>
        </div>
    );

    const migas = enCarpetas && (
        <p className='migas_admin'>
            <button type='button' onClick={() => ir({ vista: 'carpetas' })}>Todas las licenciaturas</button>
            {conLicenciatura && (
                <Fragment>
                    <i className='material-icons'>chevron_right</i>
                    {conCuatrimestre ? (
                        <button type='button' onClick={() => ir({ vista: 'carpetas', licenciatura })}>{nombreLicenciatura(licenciatura)}</button>
                    ) : (
                        <span>{nombreLicenciatura(licenciatura)}</span>
                    )}
                </Fragment>
            )}
            {conCuatrimestre && (
                <Fragment>
                    <i className='material-icons'>chevron_right</i>
                    <span>{CUATRIMESTRES[cuatrimestre]} {anio}</span>
                </Fragment>
            )}
        </p>
    );

    if (!enLista) {
        return (
            <Fragment>
                <SideNav />
                <div className='row'>
                    <div id='vistas_generales' className='col s12' style={{padding:"0"}}>
                        <BarraSeccion
                            etiqueta='Archivo'
                            encabezado={encabezado}
                            ayuda={conLicenciatura ? 'Elige un cuatrimestre para ver sus solicitudes.' : 'Elige una licenciatura para ver sus cuatrimestres.'}
                            acciones={accionesModo}
                        />
                        <div className='lista_admin'>
                            {migas}
                            {!carpetas ? (
                                <div className='lista_admin_estado'><Loader /></div>
                            ) : !conLicenciatura ? (
                                porLicenciatura.length === 0 ? (
                                    <p className='lista_admin_estado'>Aún no hay solicitudes archivadas.</p>
                                ) : (
                                    <div className='carpetas_admin'>
                                        {porLicenciatura.map(({ valor, total }) => (
                                            <button type='button' key={valor} className='carpeta_admin' onClick={() => ir({ vista: 'carpetas', licenciatura: valor })}>
                                                <i className='material-icons'>folder</i>
                                                <span>
                                                    <span className='carpeta_admin_nombre'>{nombreLicenciatura(valor)}</span>
                                                    <span className='carpeta_admin_total'>{cuantas(total)}</span>
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )
                            ) : porAnio.length === 0 ? (
                                <p className='lista_admin_estado'>No hay solicitudes archivadas de esta licenciatura.</p>
                            ) : (
                                porAnio.map(grupo => (
                                    <Fragment key={grupo.anio}>
                                        <p className='carpetas_admin_anio'>{grupo.anio}</p>
                                        <div className='carpetas_admin'>
                                            {grupo.carpetas.map(c => (
                                                <button
                                                    type='button'
                                                    key={c.cuatrimestre}
                                                    className='carpeta_admin'
                                                    onClick={() => ir({ vista: 'carpetas', licenciatura, anio: String(c.anio), cuatrimestre: String(c.cuatrimestre) })}
                                                >
                                                    <i className='material-icons'>folder</i>
                                                    <span>
                                                        <span className='carpeta_admin_nombre'>{CUATRIMESTRES[c.cuatrimestre]}</span>
                                                        <span className='carpeta_admin_total'>{cuantas(c.total)}</span>
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </Fragment>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <SideNav />
            <div className='row'>
                <ListaAdmin
                    etiqueta='Archivo'
                    encabezado={encabezado}
                    ayuda='Solicitudes archivadas. Haz clic en una para ver sus datos y documentos.'
                    acciones={accionesModo}
                    migas={migas}
                    busqueda={lista.busqueda}
                    onBusqueda={lista.setBusqueda}
                    placeholder='Buscar por nombre o correo'
                    conteo={cuantas(lista.total)}
                    mostrados={lista.registros.length}
                    cargando={lista.cargando}
                    actualizando={lista.actualizando}
                    textoVacio='No hay solicitudes archivadas con estos filtros.'
                    filtros={
                        <Fragment>
                            {!enCarpetas && (
                                <Fragment>
                                    <select className='browser-default' value={licenciatura ?? 'todas'} onChange={e => lista.setFiltro('licenciatura', e.target.value === 'todas' ? undefined : e.target.value)} aria-label='Licenciatura'>
                                        <option value='todas'>Todas las licenciaturas</option>
                                        {licenciaturas.map(({ valor, etiqueta }) => <option key={valor} value={valor}>{etiqueta}</option>)}
                                    </select>
                                    <select
                                        className='browser-default'
                                        value={anio ?? 'todos'}
                                        onChange={e => lista.setFiltros(e.target.value === 'todos' ? { anio: undefined, cuatrimestre: undefined } : { anio: e.target.value })}
                                        aria-label='Año'
                                    >
                                        <option value='todos'>Todos los años</option>
                                        {anios.map(a => <option key={a} value={a}>{a}</option>)}
                                    </select>
                                    <select
                                        className='browser-default'
                                        value={cuatrimestre ?? 'todos'}
                                        onChange={e => lista.setFiltro('cuatrimestre', e.target.value === 'todos' ? undefined : e.target.value)}
                                        disabled={!anio}
                                        title={anio ? undefined : 'Elige primero un año'}
                                        aria-label='Cuatrimestre'
                                    >
                                        <option value='todos'>Todo el año</option>
                                        {CUATRIMESTRES.map((nombre, i) => <option key={i} value={i}>{nombre}</option>)}
                                    </select>
                                </Fragment>
                            )}
                            <div className='segmentado_admin' role='group' aria-label='Estado'>
                                {ESTADOS.map(({ valor, etiqueta }) => (
                                    <button type='button' key={etiqueta} aria-pressed={estado === valor} onClick={() => lista.setFiltro('estado', valor)}>
                                        {etiqueta}
                                    </button>
                                ))}
                            </div>
                        </Fragment>
                    }
                    pie={lista.total > 0 && (
                        <Paginacion pagina={lista.pagina} limite={lista.limite} total={lista.total} onPagina={lista.setPagina} onLimite={lista.setLimite} />
                    )}
                >
                    {lista.registros.map(solicitud => (
                        <FilaAdmin
                            key={solicitud._id}
                            href={rutaDetalle + solicitud._id}
                            icono={solicitud.estadoAlumno ? 'task_alt' : 'schedule'}
                            titulo={solicitud.nombre || '(Sin nombre)'}
                            subtitulo={[solicitud.email, formatearFecha(solicitud.fecha), extraFila?.(solicitud)].filter(Boolean).join(' · ')}
                            insignias={
                                <Fragment>
                                    <Insignia variante='gris'>{nombreLicenciatura(solicitud.licenciatura)}</Insignia>
                                    <Insignia variante={solicitud.estadoAlumno ? 'verde' : 'amarilla'}>{solicitud.estadoAlumno ? 'Atendida' : 'Pendiente'}</Insignia>
                                </Fragment>
                            }
                        />
                    ))}
                </ListaAdmin>
            </div>
        </Fragment>
    );
};

export default VistaArchivo;
